import { ACTION_MS, GEAR, SCORCH, SIGHTLINE_ROOMS, rotationMs } from './config.js';
import { beats, resolveItems, type Item } from './items.js';
import { doorKey, type Maze } from './maze.js';
import { DEFAULT_ORIENTATION, rotate, type Orientation, type Rotation } from './orientation.js';
import { createRng, randomInt } from './rng.js';
import { coreBand, threatBand, type CoreBand, type ThreatBand } from './sensors.js';
import { DIRECTIONS, type Vec3 } from './vec.js';

export type AgentId = string;

/** Tactical gear an agent can carry and use. */
export type GearKind = 'emp' | 'bulkhead';

/** What a player (or bot) asks to do. */
export type Action =
  | { kind: 'rotate'; rotation: Rotation }
  | { kind: 'step' }
  | { kind: 'swap'; item: Item }
  | { kind: 'emp' }
  | { kind: 'bulkhead' };

/** An action in progress. Input is locked until `endsAt`. */
export type ActiveAction =
  | { kind: 'rotate'; rotation: Rotation; startedAt: number; endsAt: number; from: Orientation; to: Orientation }
  | { kind: 'step'; startedAt: number; endsAt: number; from: number; to: number }
  | { kind: 'bonk'; startedAt: number; endsAt: number }
  | { kind: 'swap'; startedAt: number; endsAt: number; item: Item }
  | { kind: 'emp'; startedAt: number; endsAt: number }
  | { kind: 'bulkhead'; startedAt: number; endsAt: number };

export interface AgentState {
  readonly id: AgentId;
  room: number;
  orientation: Orientation;
  item: Item;
  gear: Record<GearKind, number>;
  active: ActiveAction | null;
  /** Sensors show static until this time (hit by a rival EMP). */
  jammedUntil: number;
  /** The door this agent just came through, while its next action hasn't started yet. */
  justPassed: { from: number; to: number } | null;
  steps: number;
  actions: number;
  bonks: number;
}

export type MatchEndReason = 'core' | 'encounter';

export interface MatchResult {
  winner: AgentId;
  loser: AgentId;
  reason: MatchEndReason;
  at: number;
}

export interface Seal {
  from: number;
  to: number;
  until: number;
}

export interface Cache {
  room: number;
  kind: GearKind;
}

/**
 * A very rare decoration: a tarnished mirror on one closed wall of a room. Purely cosmetic and
 * public (every player gets the full list); placed from the cube's seed, away from the core,
 * caches and spawns, so finding one tells you nothing useful.
 */
export interface Mirror {
  room: number;
  /** Which wall: the direction from the room's centre to the wall. Never a doorway. */
  wall: Vec3;
}

/** About one room in 200 has a mirror. */
export const MIRROR_RATE = 1 / 200;

/** Scorch left in a room by an agent leaving it. */
export interface Scorch {
  agent: AgentId;
  leftAt: number;
  exit: Vec3;
}

/**
 * Events for clients to animate and sound. Items are never included: they're hidden.
 * Some events are private to one agent (marked); a server must only forward those to that agent.
 */
export type MatchEvent =
  | { type: 'actionStarted'; agent: AgentId; action: ActiveAction['kind']; at: number; endsAt: number }
  | { type: 'arrived'; agent: AgentId; room: number; at: number }
  | { type: 'bonked'; agent: AgentId; at: number }
  | { type: 'swapped'; agent: AgentId; at: number }
  | { type: 'cloaked'; room: number | null; at: number }
  | { type: 'doorwayClash'; at: number }
  | { type: 'encounter'; winner: AgentId; loser: AgentId; at: number }
  | { type: 'coreClaimed'; agent: AgentId; at: number }
  /** Private to `agent` (the one who fired). */
  | { type: 'empFired'; agent: AgentId; at: number }
  /** Private to `agent` (the one jammed). */
  | { type: 'jammed'; agent: AgentId; at: number; until: number }
  | { type: 'doorSealed'; agent: AgentId; from: number; to: number; at: number; until: number }
  /** Private to `agent`. */
  | { type: 'cachePicked'; agent: AgentId; kind: GearKind; room: number; at: number }
  | { type: 'matchOver'; result: MatchResult };

export type InputRejection = 'locked' | 'over' | 'unknownAgent' | 'sameItem' | 'noCharge' | 'nothingToSeal';
export type InputResult = { ok: true; endsAt: number } | { ok: false; reason: InputRejection };

export interface AgentSetup {
  id: AgentId;
  item: Item;
  /** Defaults to the maze's spawn for this seat. */
  room?: number;
  orientation?: Orientation;
  gear?: Partial<Record<GearKind, number>>;
}

export interface MatchOptions {
  /** Supply caches; defaults to the brief's count, placed from the maze seed. 0 disables them. */
  cacheCount?: number;
  /** Mirror rooms, when already known (online clients get them from the server). */
  mirrors?: readonly Mirror[];
}

/** Everything one agent is allowed to know. Never contains the rival's item or the core's room. */
export interface AgentView {
  self: Readonly<AgentState>;
  sensors: { core: CoreBand; threat: ThreatBand; jammed: boolean };
  /** The rival, if visible through open doorways (and not cloaked in the same room). */
  rival: { room: number; orientation: Orientation; moving: { from: number; to: number; startedAt: number; endsAt: number } | null } | null;
  /** True while a same-item cloak hides the rival in this room. */
  cloaked: boolean;
  /** Sealed doors touching rooms this agent can see. */
  seals: Seal[];
  /** Caches in rooms this agent can see. */
  caches: Cache[];
  /** The rival's scorch in this agent's own room, if fresh. `exit` only for the first few seconds. */
  scorch: { ageMs: number; exit: Vec3 | null } | null;
  over: MatchResult | null;
  /** The core's room when it is in view through open doorways, else null. */
  coreRoom: number | null;
}

/**
 * A 1v1 match: the authoritative rules engine. Time-driven and deterministic: callers pass
 * `now` (ms); the engine never reads a clock. Inputs during a lock are rejected, never queued.
 */
export class Match {
  readonly maze: Maze;
  private readonly agents: Map<AgentId, AgentState> = new Map();
  private readonly order: [AgentId, AgentId];
  private cloakRoom: number | null = null;
  private events: MatchEvent[] = [];
  private _result: MatchResult | null = null;
  private readonly seals = new Map<number, Seal>();
  private readonly caches = new Map<number, Cache>();
  /** Mirror rooms in this cube (see Mirror). */
  readonly mirrors: readonly Mirror[];
  private readonly scorch = new Map<number, Scorch>();
  private clock = 0;

  constructor(maze: Maze, seats: readonly [AgentSetup, AgentSetup], options: MatchOptions = {}) {
    this.maze = maze;
    seats.forEach((seat, i) => {
      this.agents.set(seat.id, {
        id: seat.id,
        room: seat.room ?? maze.spawns[i as 0 | 1],
        orientation: seat.orientation ?? DEFAULT_ORIENTATION,
        item: seat.item,
        gear: { emp: seat.gear?.emp ?? GEAR.startingEmp, bulkhead: seat.gear?.bulkhead ?? GEAR.startingBulkhead },
        active: null,
        jammedUntil: 0,
        justPassed: null,
        steps: 0,
        actions: 0,
        bonks: 0,
      });
    });
    if (this.agents.size !== 2) throw new Error('A match needs two agents with different ids');
    this.order = [seats[0].id, seats[1].id];
    this.placeCaches(options.cacheCount ?? GEAR.cacheCount);
    this.mirrors = options.mirrors ?? this.placeMirrors();
  }

  get result(): MatchResult | null {
    return this._result;
  }

  get over(): boolean {
    return this._result !== null;
  }

  agent(id: AgentId): Readonly<AgentState> | undefined {
    return this.agents.get(id);
  }

  rivalOf(id: AgentId): AgentId {
    return this.order[0] === id ? this.order[1] : this.order[0];
  }

  /** Returns and clears the events produced since the last call. */
  takeEvents(): MatchEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  /** Every remaining cache. Full information: for spectators, admins and tests, never for players. */
  /** Every active seal, for spectators and tests only (players see seals through `viewFor`). */
  allSeals(): Seal[] {
    return [...this.seals.values()].filter((s) => this.clock < s.until);
  }

  allCaches(): Cache[] {
    return [...this.caches.values()];
  }

  /** Whether an agent can pass from `a` into adjacent room `b` at `now`: a door, not one-way against them, not sealed. */
  isPassable(a: number, b: number, now: number): boolean {
    if (!this.maze.canPass(a, b)) return false;
    const seal = this.seals.get(doorKey(a, b));
    return !seal || now >= seal.until;
  }

  /** Try to start an action. Settles anything finished by `now` first. */
  input(id: AgentId, action: Action, now: number): InputResult {
    this.advance(now);
    const agent = this.agents.get(id);
    if (!agent) return { ok: false, reason: 'unknownAgent' };
    if (this.over) return { ok: false, reason: 'over' };
    if (agent.active && now < agent.active.endsAt) return { ok: false, reason: 'locked' };

    if (action.kind === 'swap' && action.item === agent.item) return { ok: false, reason: 'sameItem' };
    if ((action.kind === 'emp' || action.kind === 'bulkhead') && agent.gear[action.kind] < 1) return { ok: false, reason: 'noCharge' };
    if (action.kind === 'bulkhead' && !agent.justPassed) return { ok: false, reason: 'nothingToSeal' };

    agent.actions++;
    const passed = agent.justPassed;
    agent.justPassed = null; // a bulkhead is only possible as the very next action after a step

    if (action.kind === 'rotate') {
      const to = rotate(agent.orientation, action.rotation);
      const endsAt = now + rotationMs(action.rotation);
      agent.active = { kind: 'rotate', rotation: action.rotation, startedAt: now, endsAt, from: agent.orientation, to };
      agent.orientation = to; // logical orientation changes immediately; clients animate between from/to
      this.emitStart(agent);
      return { ok: true, endsAt };
    }

    if (action.kind === 'swap') {
      const endsAt = now + ACTION_MS.swap;
      agent.active = { kind: 'swap', startedAt: now, endsAt, item: action.item };
      this.emitStart(agent);
      return { ok: true, endsAt };
    }

    if (action.kind === 'emp') {
      const endsAt = now + ACTION_MS.useGear;
      agent.gear.emp--;
      agent.active = { kind: 'emp', startedAt: now, endsAt };
      this.emitStart(agent);
      return { ok: true, endsAt };
    }

    if (action.kind === 'bulkhead') {
      const endsAt = now + ACTION_MS.useGear;
      agent.gear.bulkhead--;
      agent.active = { kind: 'bulkhead', startedAt: now, endsAt };
      // The door slams as soon as the bulkhead is triggered: that's what makes it a getaway.
      const seal: Seal = { from: passed!.from, to: passed!.to, until: now + GEAR.bulkheadMs };
      this.seals.set(doorKey(seal.from, seal.to), seal);
      this.emitStart(agent);
      this.events.push({ type: 'doorSealed', agent: agent.id, from: seal.from, to: seal.to, at: now, until: seal.until });
      return { ok: true, endsAt };
    }

    const next = this.maze.neighbor(agent.room, agent.orientation.facing);
    if (next < 0 || !this.isPassable(agent.room, next, now)) {
      const endsAt = now + ACTION_MS.bonk;
      agent.bonks++;
      agent.active = { kind: 'bonk', startedAt: now, endsAt };
      this.emitStart(agent);
      this.events.push({ type: 'bonked', agent: agent.id, at: now });
      return { ok: true, endsAt };
    }
    const endsAt = now + ACTION_MS.step;
    agent.active = { kind: 'step', startedAt: now, endsAt, from: agent.room, to: next };
    this.scorch.set(agent.room, { agent: agent.id, leftAt: now, exit: agent.orientation.facing });
    this.emitStart(agent);

    // Doorway clash: the rival is passing through the same door the opposite way right now.
    const rival = this.agents.get(this.rivalOf(id))!;
    const ra = rival.active;
    if (ra && ra.kind === 'step' && ra.from === next && ra.to === agent.room && now < ra.endsAt) {
      this.events.push({ type: 'doorwayClash', at: now });
      this.resolveEncounter(now, null);
    }
    return { ok: true, endsAt };
  }

  /** Complete every action that has finished by `now`, in the order they finished. */
  advance(now: number): void {
    if (now > this.clock) this.clock = now;
    for (;;) {
      let next: AgentState | null = null;
      for (const id of this.order) {
        const a = this.agents.get(id)!;
        if (a.active && a.active.endsAt <= now && (!next || a.active.endsAt < next.active!.endsAt)) next = a;
      }
      if (!next) break;
      this.complete(next);
    }
    for (const [key, seal] of this.seals) if (now >= seal.until) this.seals.delete(key);
  }

  private complete(agent: AgentState): void {
    const done = agent.active!;
    agent.active = null;
    const at = done.endsAt;
    if (done.kind === 'swap') {
      agent.item = done.item;
      this.events.push({ type: 'swapped', agent: agent.id, at });
      if (!this.over && this.cloakRoom !== null && this.sameRoom()) this.resolveEncounter(at, this.cloakRoom);
      return;
    }
    if (done.kind === 'emp') {
      this.events.push({ type: 'empFired', agent: agent.id, at });
      const rival = this.agents.get(this.rivalOf(agent.id))!;
      if (this.maze.straightDistance(agent.room, rival.room) <= GEAR.empRadius) {
        rival.jammedUntil = Math.max(rival.jammedUntil, at + GEAR.empMs);
        this.events.push({ type: 'jammed', agent: rival.id, at, until: rival.jammedUntil });
      }
      return;
    }
    if (done.kind !== 'step') return;

    agent.room = done.to;
    agent.steps++;
    agent.justPassed = { from: done.from, to: done.to };
    this.events.push({ type: 'arrived', agent: agent.id, room: agent.room, at });
    this.pickUpCache(agent, at);
    if (this.cloakRoom !== null && !this.sameRoom()) this.cloakRoom = null;
    if (!this.over && this.sameRoom() && this.cloakRoom === null) this.resolveEncounter(at, agent.room);
    if (!this.over && agent.room === this.maze.core) {
      this.events.push({ type: 'coreClaimed', agent: agent.id, at });
      this.finish(agent.id, 'core', at);
    }
  }

  private placeCaches(count: number): void {
    if (count <= 0) return;
    const rng = createRng(this.maze.seed ^ 0x2c1b3c6d);
    const spawns = this.maze.spawns; // placement depends only on the cube, so a seed always replays the same
    const kinds: GearKind[] = ['emp', 'bulkhead'];
    let placed = 0;
    for (let guard = 0; placed < count && guard < 10_000; guard++) {
      const room = randomInt(rng, this.maze.roomCount);
      if (room === this.maze.core || this.caches.has(room)) continue;
      if (spawns.some((s) => this.maze.straightDistance(s, room) < 3)) continue;
      this.caches.set(room, { room, kind: kinds[placed % 2]! });
      placed++;
    }
  }

  private placeMirrors(): Mirror[] {
    const maze = this.maze;
    if (maze.core < 0) return []; // a player's partial maze: the server sends the mirrors instead
    // Its own random stream, so adding mirrors never moves the core, spawns or caches of a seed.
    const rng = createRng(maze.seed ^ 0x5bd1e995);
    const want = Math.round(maze.roomCount * MIRROR_RATE);
    const out: Mirror[] = [];
    for (let guard = 0; out.length < want && guard < 10_000; guard++) {
      const room = randomInt(rng, maze.roomCount);
      if (maze.straightDistance(room, maze.core) < 2) continue;
      if (maze.spawns.some((s) => maze.straightDistance(s, room) < 3)) continue;
      if ([...this.caches.keys()].some((c) => maze.straightDistance(c, room) < 2)) continue;
      if (out.some((m) => maze.straightDistance(m.room, room) < 3)) continue;
      const walls = DIRECTIONS.filter((d) => {
        const next = maze.neighbor(room, d);
        return next < 0 || !maze.hasDoor(room, next);
      });
      if (walls.length === 0) continue;
      out.push({ room, wall: walls[randomInt(rng, walls.length)]! });
    }
    return out;
  }

  private pickUpCache(agent: AgentState, at: number): void {
    const cache = this.caches.get(agent.room);
    if (!cache || agent.gear[cache.kind] >= GEAR.carryLimit) return; // full: it stays for later
    agent.gear[cache.kind]++;
    this.caches.delete(agent.room);
    this.events.push({ type: 'cachePicked', agent: agent.id, kind: cache.kind, room: agent.room, at });
  }

  private sameRoom(): boolean {
    const [a, b] = this.order.map((id) => this.agents.get(id)!.room);
    return a === b;
  }

  /** Items decide instantly. A tie cloaks (room null for a doorway clash, which has no shared room). */
  private resolveEncounter(at: number, room: number | null): void {
    if (this.over) return;
    const [a, b] = this.order.map((id) => this.agents.get(id)!) as [AgentState, AgentState];
    const outcome = resolveItems(a.item, b.item);
    if (outcome === 'tie') {
      this.cloakRoom = room;
      this.events.push({ type: 'cloaked', room, at });
      return;
    }
    const winner = outcome === 'win' ? a : b;
    const loser = outcome === 'win' ? b : a;
    this.events.push({ type: 'encounter', winner: winner.id, loser: loser.id, at });
    this.finish(winner.id, 'encounter', at);
  }

  private finish(winner: AgentId, reason: MatchEndReason, at: number): void {
    this._result = { winner, loser: this.rivalOf(winner), reason, at };
    this.events.push({ type: 'matchOver', result: this._result });
  }

  private emitStart(agent: AgentState): void {
    const a = agent.active!;
    this.events.push({ type: 'actionStarted', agent: agent.id, action: a.kind, at: a.startedAt, endsAt: a.endsAt });
  }

  /** Distance-only sensor readings for one agent at the latest time the match has seen. */
  sensorsFor(id: AgentId): { core: CoreBand; threat: ThreatBand; jammed: boolean } {
    const self = this.agents.get(id);
    if (!self) throw new Error(`Unknown agent ${id}`);
    if (this.clock < self.jammedUntil) return { core: 'none', threat: 'none', jammed: true };
    const rival = this.agents.get(this.rivalOf(id))!;
    const core = coreBand(this.maze.straightDistance(self.room, this.maze.core));
    // The threat sensor fires only when the rival's item beats yours.
    const threat = beats(rival.item, self.item) ? threatBand(this.maze.straightDistance(self.room, rival.room)) : 'none';
    return { core, threat, jammed: false };
  }

  /** What `id` is allowed to know right now: the payload a server sends that player. */
  viewFor(id: AgentId): AgentView {
    const self = this.agents.get(id);
    if (!self) throw new Error(`Unknown agent ${id}`);
    const rival = this.agents.get(this.rivalOf(id))!;
    const cloaked = this.cloakRoom !== null && this.sameRoom();
    const visible = this.maze.roomsWithin([self.room], SIGHTLINE_ROOMS);
    const moving = rival.active && rival.active.kind === 'step' ? rival.active : null;
    const rivalVisible = !cloaked && (visible.has(rival.room) || (moving !== null && visible.has(moving.to)));
    const mark = this.scorch.get(self.room);
    const age = mark ? this.clock - mark.leftAt : Infinity;
    return {
      self: { ...self, gear: { ...self.gear } },
      sensors: this.sensorsFor(id),
      rival: rivalVisible
        ? {
            room: rival.room,
            orientation: rival.orientation,
            moving: moving ? { from: moving.from, to: moving.to, startedAt: moving.startedAt, endsAt: moving.endsAt } : null,
          }
        : null,
      cloaked,
      seals: [...this.seals.values()].filter((s) => this.clock < s.until && (visible.has(s.from) || visible.has(s.to))),
      caches: [...this.caches.values()].filter((c) => visible.has(c.room)),
      scorch:
        mark && mark.agent !== id && age < SCORCH.fadeMs
          ? { ageMs: age, exit: age < SCORCH.directionMs ? mark.exit : null }
          : null,
      over: this._result,
      coreRoom: visible.has(this.maze.core) ? this.maze.core : null,
    };
  }
}
